import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { RefreshCw, CameraOff } from "lucide-react";

/**
 * Self-hosted HLS webcam player.
 *
 * Loads a stream from `${REACT_APP_BACKEND_URL}${streamUrl}` (e.g.
 * /api/webcams/hutchison/playlist.m3u8). Uses native HLS on Safari/iOS and
 * hls.js everywhere else. The `refreshKey` prop forces a full re-init when
 * the user clicks the refresh button.
 *
 * If the upstream stream is unavailable (e.g. backend returns 503 because the
 * ffmpeg pipeline can't reach the camera), shows an "offline" overlay rather
 * than a forever-spinning loader.
 */
const WebcamPlayer = ({ streamUrl, refreshKey, onRefresh, className = "" }) => {
  const videoRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | live | offline

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !streamUrl) return;

    const fullUrl = `${process.env.REACT_APP_BACKEND_URL || ""}${streamUrl}`;
    let hls = null;
    let networkRetries = 0;
    const MAX_NETWORK_RETRIES = 3;

    setStatus("loading");

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari / iOS — native HLS support
      video.src = fullUrl;
      const onPlaying = () => setStatus("live");
      const onError = () => setStatus("offline");
      video.addEventListener("playing", onPlaying);
      video.addEventListener("error", onError);
      video.play().catch(() => {});
      return () => {
        video.removeEventListener("playing", onPlaying);
        video.removeEventListener("error", onError);
        video.removeAttribute("src");
        video.load();
      };
    }

    if (Hls.isSupported()) {
      hls = new Hls({
        liveDurationInfinity: true,
        lowLatencyMode: false,
        backBufferLength: 30,
        manifestLoadingMaxRetry: 2,
        manifestLoadingRetryDelay: 2000,
        levelLoadingMaxRetry: 2,
      });
      hls.loadSource(fullUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        networkRetries = 0;
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.FRAG_LOADED, () => {
        networkRetries = 0;
        setStatus("live");
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            networkRetries += 1;
            if (networkRetries >= MAX_NETWORK_RETRIES) {
              setStatus("offline");
              hls.destroy();
            } else {
              hls.startLoad();
            }
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError();
            break;
          default:
            setStatus("offline");
            hls.destroy();
            break;
        }
      });
      return () => {
        if (hls) {
          hls.destroy();
        }
      };
    }

    // Fallback: no HLS support at all (very old browsers)
    setStatus("offline");
    return undefined;
  }, [streamUrl, refreshKey]);

  return (
    <div className={`relative bg-black rounded-lg overflow-hidden ${className}`}>
      <div className="w-full aspect-video">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          controls
          className="w-full h-full object-cover"
        />
      </div>

      {status === "offline" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/85 text-center p-6 pointer-events-none">
          <CameraOff className="w-12 h-12 text-gray-400 mb-3" />
          <p className="text-white font-semibold text-lg">Camera temporarily offline</p>
          <p className="text-gray-300 text-sm mt-1">
            Reconnecting automatically — try the refresh button shortly.
          </p>
        </div>
      )}

      <div className="absolute top-4 left-4 pointer-events-none">
        {status === "live" ? (
          <Badge className="bg-red-600 hover:bg-red-700 animate-pulse">
            🔴 LIVE
          </Badge>
        ) : status === "offline" ? (
          <Badge className="bg-gray-600">OFFLINE</Badge>
        ) : (
          <Badge className="bg-yellow-600 animate-pulse">CONNECTING…</Badge>
        )}
      </div>
      <div className="absolute top-4 right-4">
        <Button
          size="sm"
          variant="secondary"
          className="bg-black/50 hover:bg-black/70 text-white border-0"
          onClick={onRefresh}
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default WebcamPlayer;
